const epiPropertyName = "data-epi-property-name";
const epiEdit = "data-epi-edit";
let safeCrypto: Crypto;

function uuidFallback() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
        (parseInt(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (parseInt(c) / 4)))).toString(16)
    ) as `${string}-${string}-${string}-${string}-${string}`;
}

if (window && "crypto" in window && "randomUUID" in window.crypto) {
    // it is always defined in https context
    safeCrypto = window.crypto;
} else {
    // fallback for http contexts based on the getRandomValues method that is always available
    safeCrypto = window.crypto ?? ({ randomUUID: uuidFallback } as Crypto);
    safeCrypto.randomUUID = uuidFallback;
}
interface DocumentProperty {
    name: string;
    tagName: string;
    uniqueName: string;
    dataset: any;
    blockNodes: DocumentProperty[] | undefined;
    innerHTML: string;
    style: any;
    offsetLeft: number;
    offsetTop: number;
    offsetHeight: number;
    offsetWidth: number;
}

function getPropertyNodes(doc: Document) {
    const allNodes = Array.from(doc.querySelectorAll("[data-epi-edit], [data-epi-property-name]"));
    // Write out all the combinations of nested nodes to avoid runtime calculations which would always give the same result.
    const nestedNodes = Array.from(
        doc.querySelectorAll(
            "[data-epi-edit] [data-epi-edit], [data-epi-property-name] [data-epi-property-name], [data-epi-property-name] [data-epi-edit], [data-epi-edit] [data-epi-property-name]"
        )
    );

    return allNodes.filter(function (node) {
        // return the nodes that are not nested
        return nestedNodes.indexOf(node) === -1;
    });
}

// each property will have unique name that helps when refreshing when size changed
function assignIds(array: DocumentProperty[]) {
    const names = array.map(function (x) {
        return x.name;
    });

    array.forEach(function (x, index) {
        // when property name occurs only once then it's a unique value
        if (names.filter((y) => y === x.name).length === 1) {
            x.uniqueName = x.name;
            return;
        }

        // when more than once, then add name and unique hash
        const postfix = safeCrypto.randomUUID();
        x.uniqueName = x.name + "_" + postfix;
    });
}

function getMarginExtents(node: Element, computedStyle: CSSStyleDeclaration): any {
    const toPixelValue = (element: Element, value: string | null): number => {
        // style values can be floats, client code may want
        // to round for integer pixels.
        return parseFloat(value || "") || 0;
    };

    const l = toPixelValue(node, computedStyle.marginLeft),
        t = toPixelValue(node, computedStyle.marginTop),
        r = toPixelValue(node, computedStyle.marginRight),
        b = toPixelValue(node, computedStyle.marginBottom);
    return { l: l, t: t, r: r, b: b, w: l + r, h: t + b };
}

// negative margin affects size of the overlays
// we need to calculate them and adjust the size
function adjustMargins(property: DocumentProperty, node: Element) {
    if (!node.ownerDocument || !node.ownerDocument.defaultView) {
        return;
    }

    const computedStyle =
        node.nodeType === 1 /* ELEMENT_NODE*/ ? node.ownerDocument.defaultView.getComputedStyle(node, null) : null;

    if (!computedStyle) {
        return;
    }

    // deal with negative margins
    const margin = getMarginExtents(node, computedStyle);

    if (margin.l < 0) {
        property.offsetLeft -= margin.l;
        property.offsetWidth += margin.l;
    }
    if (margin.r < 0) {
        property.offsetWidth -= margin.r;
    }
    if (margin.t < 0) {
        property.offsetTop -= margin.t;
        property.offsetHeight += margin.t;
    }
    if (margin.b < 0) {
        property.offsetHeight -= margin.b;
    }
}

function constructDocumentProperty(elem: Element, ignoreBlockNodes = false) {
    const htmlElem = elem as HTMLElement;
    const rect = elem.getBoundingClientRect();
    const computedStyle = getComputedStyle(htmlElem);
    const paddingTop = parseInt(computedStyle.paddingTop, 10);
    const paddingLeft = parseInt(computedStyle.paddingLeft, 10);
    const paddingRight = parseInt(computedStyle.paddingRight, 10);
    const paddingBottom = parseInt(computedStyle.paddingBottom, 10);

    const propertyName = elem.getAttribute(epiPropertyName) || elem.getAttribute(epiEdit);
    const property: DocumentProperty = {
        name: propertyName || "",
        uniqueName: propertyName ? propertyName + safeCrypto.randomUUID() : safeCrypto.randomUUID(),
        tagName: elem.tagName,
        dataset: Object.assign({}, htmlElem.dataset),
        blockNodes: undefined,
        innerHTML: elem.innerHTML,
        style: Object.assign({}, htmlElem.style),
        offsetHeight: rect.height - (ignoreBlockNodes ? paddingTop + paddingBottom : 0),
        offsetLeft: rect.left + (ignoreBlockNodes ? paddingLeft : 0),
        offsetTop: rect.top + (ignoreBlockNodes ? paddingTop : 0),
        offsetWidth: rect.width - (ignoreBlockNodes ? paddingLeft + paddingRight : 0),
    };
    adjustMargins(property, elem);

    if (!ignoreBlockNodes) {
        const blockNodes = getBlockNodes(elem);
        if (blockNodes && blockNodes.length > 0) {
            property.blockNodes = blockNodes.map((x) => constructDocumentProperty(x, true));
        }
    }
    return property;
}

function getBlockNodes(elem: Element) {
    let allBlockNodes = Array.from(elem.querySelectorAll("[data-epi-block-id]"));
    let nestedBlockNodes = Array.from(elem.querySelectorAll("[data-epi-block-id] [data-epi-block-id]"));

    // support contentGuid
    if (allBlockNodes.length === 0) {
        allBlockNodes = Array.from(elem.querySelectorAll("[data-epi-content-id]"));
        nestedBlockNodes = Array.from(elem.querySelectorAll("[data-epi-content-id] [data-epi-content-id]"));
    }

    return allBlockNodes.filter(function (node) {
        // return the blocks that are not nested
        return nestedBlockNodes.indexOf(node) === -1;
    });
}

const buildPropertyCollection = () => {
    // collect data about the properties and its positions and send to server for use in calculating bounding boxes in overlay.
    const properties: DocumentProperty[] = [];
    getPropertyNodes(document).forEach(function (elem) {
        properties.push(constructDocumentProperty(elem));
    });
    assignIds(properties);
    return properties;
};

const buildFullReloadPropertyCollection = (): string[] => {
    let fullReloadProperties: string[] = [];
    const element = document.querySelector(
        "input[data-epi-full-refresh-property-names][type='hidden']"
    ) as HTMLInputElement;
    if (element?.dataset?.epiFullRefreshPropertyNames) {
        fullReloadProperties = element.dataset.epiFullRefreshPropertyNames.split(",");
    }

    return fullReloadProperties;
};

export { buildPropertyCollection, buildFullReloadPropertyCollection };
